import { useRef } from 'react';

import lowerCase from 'lodash/lowerCase';

import { useMutation, useQuery, useQueryClient } from '@lumapps/base-api/react-query';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL } from '@lumapps/translations';

import * as postApi from '../../../api';
import { votesQueryKeys } from '../../../api';
import { IdeaVote, Post, PostVotes } from '../../../types';

export const usePostVote = ({ initialVotes, postId }: { initialVotes: PostVotes; postId: string }) => {
    const queryClient = useQueryClient();
    const notify = useNotification();

    const { data: votes } = useQuery<PostVotes>({
        queryKey: votesQueryKeys.votes(postId),
        initialData: initialVotes,
        enabled: false,
    });

    const state = votes ?? initialVotes;

    // Saving current state
    const stateBeforeVoteRef = useRef(state);

    const onVoteMutation = useMutation({
        mutationFn: (vote: IdeaVote) => postApi.vote(vote, postId),
        mutationKey: votesQueryKeys.votes(postId),
        onMutate(vote: IdeaVote) {
            // Saving previous state in case of rollback needed
            stateBeforeVoteRef.current = state;
            // Optimistic update
            const previousUserVoteImpact = state.userVote && lowerCase(state.userVote) !== IdeaVote.NONE ? -1 : 0;
            queryClient.setQueryData(votesQueryKeys.votes(postId), {
                downVotes: vote === IdeaVote.DOWN ? state.downVotes + 1 : state.downVotes + previousUserVoteImpact,
                upVotes: vote === IdeaVote.UP ? state.upVotes + 1 : state.upVotes + previousUserVoteImpact,
                userVote: vote,
            });
        },
        onSuccess({ data }: { data: Post }) {
            // Update cache
            queryClient.setQueryData(votesQueryKeys.votes(postId), {
                downVotes: data?.score?.votesDown,
                upVotes: data?.score?.votesUp,
                userVote: data.userVote,
            });
        },
        onError() {
            // Generic error notification
            notify.error({ translate: GLOBAL.GENERIC_ERROR });
            // Rollback optimistic update
            queryClient.setQueryData(votesQueryKeys.votes(postId), stateBeforeVoteRef.current);
        },
    });

    const onDeleteVoteMutation = useMutation({
        mutationFn: () => postApi.deleteVote(postId),
        mutationKey: votesQueryKeys.votes(postId),
        onMutate() {
            // Saving previous state in case of rollback needed
            stateBeforeVoteRef.current = state;
            // Optimistic update
            queryClient.setQueryData(votesQueryKeys.votes(postId), {
                downVotes: state.userVote === IdeaVote.DOWN ? state.downVotes - 1 : state.downVotes,
                upVotes: state.userVote === IdeaVote.UP ? state.upVotes - 1 : state.upVotes,
                userVote: null,
            });
        },
        onSuccess({ data }: { data: Post }) {
            // Update cache
            queryClient.setQueryData(votesQueryKeys.votes(postId), {
                downVotes: data?.score?.votesDown,
                upVotes: data?.score?.votesUp,
                userVote: data.userVote,
            });
        },
        onError() {
            // Generic error notification
            notify.error({ translate: GLOBAL.GENERIC_ERROR });
            // Rollback optimistic update
            queryClient.setQueryData(votesQueryKeys.votes(postId), stateBeforeVoteRef.current);
        },
    });

    return {
        votes: state,
        onVote: onVoteMutation.mutate,
        onDeleteVote: onDeleteVoteMutation.mutate,
    };
};
